/* PlannerTierCards.jsx — compact tier picker tuned for the planner's
 * "Author the case" page.
 *
 * iter98.99at — Aligned with the latest commercial tier shape that the
 * Admin Pricing form now manages (post drop-12-fields cleanup): only
 * commercial deliverables show — aligner RANGE (aligner_min →
 * aligner_count), refinement count + cap, retainer count. Marketing
 * ribbons (Recommended / Overage) were removed because the planner
 * authors the aligner count themselves and the doctor side auto-detects
 * the tier — surfacing a "Recommended" pill on top of that just adds
 * noise. The selected check + clean ring remains the primary affordance.
 *
 * Renders 4-across on lg screens, 2-across on md, 1-up on mobile.
 */
import React from "react";
import { CheckCircle } from "@phosphor-icons/react";


/* iter98.99co — `inr()` helper kept on standby for future surfaces (e.g. an
   admin-impersonation view) but no longer called in the planner UI since
   we suppress all tier-price displays here. */
// eslint-disable-next-line no-unused-vars
function inr(n) {
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return `₹${Math.round(v).toLocaleString("en-IN")}`;
}


/* Format the stage range a tier covers. Falls back to "Up to Y" when
   `aligner_min` is 0/unset (legacy tier or first tier in the ladder).
   iter98.99da — Renamed user-facing label "aligners" → "stages" since
   the tier counts STAGES of treatment (1 stage = 1 upper + 1 lower
   aligner set). The DB key `aligner_min/aligner_count` is kept for
   backwards-compat. */
function rangeLabel(mn, mx) {
    const min = Number(mn) || 0;
    const max = Number(mx) || 0;
    if (max <= 0) return "—";
    if (min > 0) return `${min}–${max} stages`;
    return `Up to ${max} stages`;
}


export default function PlannerTierCards({
    tiers,
    selectedTierId,
    onSelect,
    autoSelected = false,
}) {
    if (!tiers || tiers.length === 0) {
        return (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                No active treatment tiers configured. Ask admin to set them up under Pricing.
            </div>
        );
    }

    return (
        <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5"
            data-testid="planner-tier-cards"
        >
            {tiers.map((t) => {
                const selected = selectedTierId === t.id;
                /* iter98.99cr — When the tier was auto-matched from the
                   planner's aligner count, all non-matching tiers go into
                   a locked / disabled state. Rationale: the tier IS a
                   function of the aligner count (it lives within an
                   aligner_min–aligner_count range). Letting the planner
                   override creates plan↔commercial drift (e.g. a 20-aligner
                   case billed under Lite). To change tier, the planner
                   must update the aligner count in Section 06 — that's
                   the canonical clinical input.

                   When `autoSelected` is false (e.g. count out of range,
                   or no count yet), all cards stay clickable so the
                   planner can pick manually. */
                const locked = autoSelected && !selected;
                /* iter98.99co — lab_charge intentionally NOT read for the
                   planner UI. External / outsourced planners are clinical
                   contractors and shouldn't see Invisique's commercial
                   terms. Tier selection is driven by aligner counts +
                   inclusions, not price. */
                const refCount = Number(t.refinement_count || 0);
                const refCap = Number(t.refinement_aligner_count || 0);
                const retCount = Number(t.included_retainer_count || 0);

                return (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => { if (!locked) onSelect(t); }}
                        disabled={locked}
                        aria-disabled={locked}
                        data-testid={`planner-tier-card-${t.id}`}
                        data-locked={locked ? "1" : undefined}
                        title={locked ? "Update the aligner count in Section 06 to change tier" : undefined}
                        className={`relative text-left rounded-xl border p-3 transition-all duration-150 ${
                            selected
                                ? "border-emerald-500 ring-2 ring-emerald-500/20 bg-gradient-to-br from-emerald-50 to-emerald-100/40 shadow-sm"
                                : locked
                                    ? "border-border bg-muted/40 opacity-50 cursor-not-allowed grayscale"
                                    : "border-border hover:border-emerald-300 hover:shadow-sm bg-card"
                        }`}
                    >
                        {/* Selected check — dual-mode: explicit pick = solid emerald,
                            auto-matched = same emerald with a tiny "AUTO" caption
                            tucked beneath so it's discoverable without being loud. */}
                        {selected && (
                            <div
                                className="absolute top-2 right-2 flex flex-col items-end gap-0.5"
                                data-testid={`planner-tier-card-${t.id}-selected`}
                            >
                                <div className="w-5 h-5 rounded-full bg-emerald-600 text-white grid place-items-center">
                                    <CheckCircle size={12} weight="fill" />
                                </div>
                                {autoSelected && (
                                    <span
                                        className="text-[8px] font-bold uppercase tracking-[0.12em] text-emerald-700/80 leading-none"
                                        data-testid={`planner-tier-card-${t.id}-auto-tag`}
                                    >
                                        Auto
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="font-display font-semibold text-sm leading-tight pr-6">
                            {t.name}
                        </div>
                        {/* iter98.99co — Tier price suppressed for planners.
                            Aligner range becomes the primary differentiator. */}
                        <div className="text-[11px] text-muted-foreground mt-2">
                            {rangeLabel(t.aligner_min, t.aligner_count)}
                        </div>
                        {(t.refinement_included || t.retainers_included) && (
                            <div className="text-[10px] text-emerald-700 font-semibold mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5">
                                {t.refinement_included && (
                                    <span
                                        className="inline-flex items-center gap-0.5"
                                        data-testid={`planner-tier-card-${t.id}-refinement`}
                                    >
                                        <CheckCircle size={9} weight="fill" />
                                        {refCount > 0 ? `${refCount}× refinement` : "Refinement"}
                                        {refCap > 0 && (
                                            <span className="font-normal text-emerald-700/80"> (up to {refCap} stages)</span>
                                        )}
                                    </span>
                                )}
                                {t.retainers_included && (
                                    <span
                                        className="inline-flex items-center gap-0.5"
                                        data-testid={`planner-tier-card-${t.id}-retainers`}
                                    >
                                        <CheckCircle size={9} weight="fill" />
                                        {retCount > 0 ? `${retCount}× retainer${retCount === 1 ? "" : "s"}` : "Retainers"}
                                    </span>
                                )}
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
